import { useEffect, useMemo, useRef, useState } from 'react'
import {
  bandFor, endpoints, readoutFor,
  type AgendaItem, type AttributeCatalog, type BoardMember,
  type Commitment, type Consult, type HomeMetrics, type NextMeeting,
} from '../../api/client'
import { Avatar, ramp } from '../../components/Avatar'
import { SeatPicker } from '../../components/SeatPicker'
import { daysUntil, formatDay, parseDay } from '../../dates'
import { promptsFor } from '../../prompts'
import { useTrade } from '../../trade'
import { capturePhoto, isNativeApp } from '../../native'
import { TopicIcon } from '../../components/TopicIcon'
import { topicFor } from '../../topics'
import { summarise } from '../../summarise'


/*
  Home widgets (UI SPEC §4).

  Every one of them follows the same three rules, because the old Home broke all three:

  1. A widget owns its own fetch and its own skeleton. One slow read must never hold the
     page, and a full-page spinner for six independent things is the easiest way to make a
     fast product feel slow.

  2. Empty states are written as a prompt to act, never as a null result. "Nothing here"
     tells an owner the product has nothing to offer; saying what the board will do if he
     adds nothing tells him it is already working.

  3. Each header carries a chevron to the full screen. The widget is a way in, not a
     replacement.
*/

export function WidgetHeader({
  title, count, action, onNavigate, live,
}: {
  title: string
  count?: number
  action?: string
  onNavigate?: () => void
  /** Something here is late or disputed. Says so in ink instead of quiet uppercase. */
  live?: boolean
}) {
  /*
    The heading is the link, with the arrow set against the title rather than a separate
    control on the far right.

    The old shape put the name on the left and "All meetings ›" on the right, which reads
    as two things and makes the owner find the small one. One target, and the arrow sitting
    against the words is what says the whole section continues somewhere. `action` survives
    as the accessible name, because "Actions →" read aloud does not say where it goes.
  */
  const inner = (
    <>
      <span className={`w__title${live ? ' w__title--live' : ''}`}>{title}</span>
      {/* "1 on the table" as a heading read oddly. The name stays fixed and the
          number becomes a pill, which is also what makes it scannable. */}
      {count !== undefined && count > 0 && <span className="count-pill">{count}</span>}
      {onNavigate && <span className="w__arrow" aria-hidden>→</span>}
    </>
  )

  return (
    <div className="w__head">
      {onNavigate ? (
        <button
          className="w__head-left w__head-left--go"
          onClick={onNavigate}
          aria-label={action ?? `Open ${title}`}
        >
          {inner}
        </button>
      ) : (
        <span className="w__head-left">{inner}</span>
      )}
    </div>
  )
}

export function Skeleton({ rows = 2 }: { rows?: number }) {
  return (
    <div className="w__skeleton">
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="skeleton" style={{ width: `${88 - i * 18}%` }} />
      ))}
    </div>
  )
}

// ─── 1. Board roster ────────────────────────────────────────────────────────

export function Roster({
  members, onOpenSeat, onScopeAsk, scopedTo,
}: {
  members: BoardMember[]
  onOpenSeat: (id: string) => void
  onScopeAsk: (member: BoardMember | null) => void
  scopedTo: BoardMember | null
}) {
  const [catalog, setCatalog] = useState<AttributeCatalog | null>(null)

  useEffect(() => {
    void endpoints.attributes().then(setCatalog).catch(() => setCatalog(null))
  }, [])

  // Computed for the whole roster at once, because the point is that the lines differ
  // from each other.
  const lines = useMemo(
    () => (catalog ? describeAll(members, catalog) : new Map<string, string>()),
    [members, catalog])

  if (members.length === 0) return null

  return (
    <section className="w w--roster">
      <WidgetHeader title="Your board" action="Tune" onNavigate={() => onOpenSeat('')} />

      <div className="roster">
        {members.map(m => (
          <div
            key={m.id}
            className={`roster__seat ${scopedTo?.id === m.id ? 'is-scoped' : ''}`}
          >
            <button
              className="roster__face"
              onClick={() => onOpenSeat(m.id)}
              title={`Open ${m.displayName}`}
            >
              <Avatar member={m} size="lg" />
            </button>

            <span className="roster__name">{m.displayName} · {seatLabel(m)}</span>

            {/* Every seat gets its own row for this now. Behind a hover it was a line
                most owners would never have read. */}
            <span className="roster__line">{lines.get(m.id) ?? ' '}</span>

            {/*
              "Ask" rather than "Ask just Manny", because the button now stands at rest in
              its own column and the long label would take the disposition line's room.
              Sitting on the person's own row carries the meaning; the full sentence stays
              on the element for anyone who cannot see where it sits.
            */}
            <button
              className="roster__scope"
              aria-pressed={scopedTo?.id === m.id}
              title={scopedTo?.id === m.id
                ? `Asking ${m.displayName.split(' ')[0]} alone. Put it back to the whole board.`
                : `Ask just ${m.displayName.split(' ')[0]}`}
              aria-label={scopedTo?.id === m.id
                ? `Stop asking ${m.displayName.split(' ')[0]} alone`
                : `Ask just ${m.displayName.split(' ')[0]}`}
              onClick={() => onScopeAsk(scopedTo?.id === m.id ? null : m)}
            >
              {scopedTo?.id === m.id ? 'Asking' : 'Ask'}
            </button>
          </div>
        ))}
      </div>
    </section>
  )
}

function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function seatLabel(member: BoardMember): string {
  if (member.seatRole === 'chair') return 'chair'
  if (member.seatRole === 'dissenter') return 'dissent'

  const strongest = Object.entries(member.attributes.competence).sort((a, b) => b[1] - a[1])[0]
  return strongest && strongest[1] >= 7 ? (DOMAIN_LABELS[strongest[0]] ?? 'member') : 'member'
}

const DOMAIN_LABELS: Record<string, string> = {
  finance: 'finance', sales_pricing: 'sales', marketing: 'marketing', operations: 'ops',
  people: 'people', systems_tech: 'systems', trade_craft: 'the trade', exit_ma: 'exit',
}

/*
  One line per seat, and no two seats allowed to open with the same phrase.

  The band wording is already unique: every attribute has four distinct readouts and none
  is reused across attributes. The rhyming came from somewhere else. Priya and Sam are
  both very_high on data_orientation, so both led with "won't move without numbers";
  Manny and Ruth are both high on bluntness, so both said "says it straight". Picking
  each member's three most extreme traits independently guarantees that collision on any
  board where two people are genuinely alike.

  So the phrases are claimed across the roster. A seat that would have repeated an
  earlier seat's (attribute, band) falls through to its next most extreme trait, which is
  the one that actually distinguishes it. Members are walked in seat order so the result
  is stable rather than depending on render order.
*/
function describeAll(
  members: BoardMember[], catalog: AttributeCatalog,
): Map<string, string> {
  const byKey = new Map(catalog.definitions.map(d => [d.key, d]))
  const claimed = new Set<string>()
  const lines = new Map<string, string>()

  for (const member of [...members].sort((a, b) => a.seatOrder - b.seatOrder)) {
    const ranked = Object.entries(member.attributes.disposition)
      .map(([key, value]) => ({ key, value, distance: Math.abs(value - 5) }))
      .filter(t => t.distance > 1 && byKey.has(t.key))
      .sort((a, b) => b.distance - a.distance || a.key.localeCompare(b.key))

    const traits: string[] = []

    // First pass takes only phrases nobody else has used, so each line opens on what
    // makes this seat different rather than on whatever it happens to score highest.
    for (const t of ranked) {
      if (traits.length === 3) break
      const claim = `${t.key}:${bandFor(t.value)}`
      if (claimed.has(claim)) continue

      const readout = readoutFor(byKey.get(t.key)!, t.value)
      if (!readout) continue

      claimed.add(claim)
      traits.push(readout.toLowerCase())
    }

    // A seat whose every trait was already spoken for still gets a line. Two genuinely
    // similar members should read similarly; they just must not read identically, and
    // the ordering above has already given them different openings where it could.
    if (traits.length === 0) {
      for (const t of ranked.slice(0, 2)) {
        const readout = readoutFor(byKey.get(t.key)!, t.value)
        if (readout) traits.push(readout.toLowerCase())
      }
    }

    // The name is already on the row above, so the line is just the disposition.
    lines.set(member.id, traits.length === 0
      ? 'Balanced across the board.'
      : `${sentenceCase(traits.join(', '))}.`)
  }

  return lines
}

// ─── 2. Ask box ─────────────────────────────────────────────────────────────

/*
  Real questions an owner would actually type, rotated per load. A single fixed example
  starts to read as chrome after the third visit; five make the box feel like it is
  waiting for something specific.

  The five come from prompts.ts and depend on the trade. They used to be a fixed list about
  vans, techs and callout fees shown to every business on the platform, which told anyone
  who was not a trades company that the product had been built for somebody else, before
  they had typed a word.
*/

/* What the picker offers. The same list the table's own uploader uses; the server is the
   real gate on all of it. */
const ASK_ACCEPT = [
  'image/*', '.heic', '.heif',
  'application/pdf', '.pdf',
  '.xls', '.xlsx', '.csv',
  '.doc', '.docx', '.txt', '.rtf', '.pages', '.numbers',
].join(',')

function IconClip() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M21.4 11.05 12.25 20.2a5.5 5.5 0 1 1-7.78-7.78l9.2-9.19a3.67 3.67 0 0 1 5.18 5.19l-9.19 9.19a1.83 1.83 0 0 1-2.6-2.6l8.5-8.48" />
    </svg>
  )
}

function IconCamera() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.2a1.5 1.5 0 0 0 1.24-.66l.72-1.08A1.5 1.5 0 0 1 9.9 4.6h4.2a1.5 1.5 0 0 1 1.24.66l.72 1.08A1.5 1.5 0 0 0 17.3 7h2.2A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z" />
      <circle cx="12" cy="13" r="3.4" />
    </svg>
  )
}

export function AskBox({
  scopedTo, board, onScopeAsk, onAsked, onCaptured,
}: {
  scopedTo: BoardMember | null
  board: BoardMember[]
  onScopeAsk: (member: BoardMember | null) => void
  onAsked: (consultId: string) => void
  onCaptured: (text: string, documentIds?: string[]) => Promise<void>
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState<'ask' | 'table' | null>(null)
  const [picking, setPicking] = useState(false)

  /*
    Files chosen but not yet sent.

    Held here rather than uploaded on pick, because a question is one act: an owner who
    attaches a photo and then changes his mind should not have left a document behind on the
    profile. They go up when he presses the button, and the question claims them before
    anything reaches the board.
  */
  const [files, setFiles] = useState<File[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)
  const trade = useTrade()
  const placeholder = useMemo(() => {
    const options = promptsFor(trade)
    return options[Math.floor(Math.random() * options.length)]!
  }, [trade])
  const box = useRef<HTMLTextAreaElement>(null)

  // Grows with what is typed, from three rows to eight. A fixed 300px box sat empty on
  // every visit and made the widest column on the page look unfinished.
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    const line = parseFloat(getComputedStyle(el).lineHeight) || 24
    // Two rows at rest, growing to eight. A tall empty box on a phone is most of the
    // card spent on nothing.
    el.style.height = `${Math.min(Math.max(el.scrollHeight, line * 2), line * 8)}px`
  }, [text])

  const submit = async (mode: 'ask' | 'table') => {
    const value = text.trim()

    // Never disabled. A greyed primary on an empty box read as broken rather than as
    // waiting, and the fix for "nothing typed yet" is a cursor, not a dead button.
    if (!value) { box.current?.focus(); return }
    if (busy) return

    setBusy(mode)
    setUploadError(null)
    try {
      /*
        Files first, then the question.

        This order is the whole feature. The worker waits for attachments it can see, so a
        file linked a moment after the consult was queued is a file the board never knew to
        wait for: it answers about a statement it has not read, in front of the owner who
        just handed it over.

        A file that fails to upload does not take the question with it. Losing what somebody
        typed because a photo was too large is worse than answering without the photo, so
        the failure is reported and the question still goes.
      */
      let documentIds: string[] = []

      if (files.length > 0) {
        const results = await Promise.allSettled(files.map(f => endpoints.uploadFile(f)))
        documentIds = results.flatMap(r => r.status === 'fulfilled' ? [r.value.id] : [])

        const failed = results.length - documentIds.length
        if (failed > 0) {
          setUploadError(failed === results.length
            ? 'Those files did not upload, so the board is answering without them.'
            : `${failed} of those files did not upload.`)
        }
      }

      if (mode === 'ask') {
        const { id } = await endpoints.ask(value, scopedTo?.id, documentIds)
        onAsked(id)
      } else {
        await onCaptured(value, documentIds)
      }
      setText('')
      setFiles([])
      onScopeAsk(null)
    } finally {
      setBusy(null)
    }
  }

  const addFiles = (chosen: FileList | null) => {
    if (!chosen || chosen.length === 0) return
    setUploadError(null)
    // Four is more than anyone has attached to one question, and it is what the board reads
    // under an agenda item before the prompt starts costing more than the answer.
    setFiles(current => [...current, ...Array.from(chosen)].slice(0, 4))
  }

  /**
   * The same list, arrived at through the camera instead of the file sheet.
   *
   * Goes through setFiles directly rather than addFiles because addFiles takes a FileList
   * and there is no way to build one; the four-file cap is repeated here rather than
   * worked around, since a photograph is a file like any other.
   *
   * A camera that will not open says so in the same line an upload failure uses. Closing
   * the shutter says nothing, because that was a decision rather than a fault.
   */
  const takePhoto = async () => {
    const result = await capturePhoto()
    if ('cancelled' in result) return
    if ('error' in result) { setUploadError(result.error); return }

    setUploadError(null)
    setFiles(current => [...current, result.photo].slice(0, 4))
  }

  return (
    <section className="w w--ask">
      <WidgetHeader title={scopedTo ? `Ask ${scopedTo.displayName}` : 'Ask the board'} />

      {scopedTo && (
        <button className="ask__scope" onClick={() => onScopeAsk(null)}>
          <Avatar member={scopedTo} size="sm" />
          <span>
            Just {scopedTo.displayName.split(' ')[0]}, on {scopedTo.discipline.toLowerCase()}.
            Tap to hand it back to the chair.
          </span>
        </button>
      )}

      {/*
        The box and the two things you do to what is in it, as one field.

        Attach and Snap were a row of labelled buttons under the send buttons, and between
        the two rows the card spent about ninety pixels on furniture before it got to
        Recently. Recently is what makes this feel like a board that has been running rather
        than a form, so it has to be on the screen. They are icons at the trailing edge of
        the box now, which is where every messaging app on this owner's phone puts them, and
        the box reserves room for them so a long question never runs underneath.
      */}
      <div className="ask__field">
        <textarea
          className="ask__box"
          ref={box}
          value={text}
          rows={2}
          placeholder={placeholder}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit('ask') }
          }}
        />

      </div>

      {/*
        What is going with the question.

        Listed above the buttons rather than below them, because it is part of what is about
        to be sent. Each one can be taken off again: attaching the wrong photo and having no
        way back is how an owner ends up with a document on his profile he never meant to
        put there.
      */}
      {files.length > 0 && (
        <ul className="ask__files">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              <span className="ask__filename">{f.name}</span>
              <button
                className="ask__fileoff"
                type="button"
                aria-label={`Remove ${f.name}`}
                disabled={!!busy}
                onClick={() => setFiles(current => current.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {uploadError && <p className="ask__error">{uploadError}</p>}

      {/* Equal weight on purpose. The table is what feeds the ritual, so making it the
          outlined secondary option quietly argued against the product's own design.

          The paperclip is neither. Attaching is not a third way to send the question, it is
          something you do to the question before you send it, so it sits apart from the two
          buttons that do. */}
      {/*
        Stacked, and each one the full width of the card.

        Side by side on a phone they were squeezed to about 110px apiece and both labels
        wrapped mid-phrase: "Ask the / board" beside "Put it on the / table". Two lines of
        broken text is not a smaller button, it is a worse one. Down the page they read as
        two whole sentences and the primary is unmistakably the primary.
      */}
      {/*
        The paperclip belongs beside the thing that sends the question, not pinned to the
        end of the box.

        It used to sit at the end of .ask__field, which is right on a phone where the box is
        the width of the card. On a desktop card of nearly nine hundred pixels the box
        stretches and the icon ends up marooned against the right edge, a long way from the
        two buttons and from anything it relates to.

        In this row it costs no height at either width: the buttons are already a row on a
        desktop and already a column on a phone, and an icon beside them fits in the space
        that column leaves.
      */}
      <div className="ask__send">
        <div className="ask__actions">
          <button className="btn btn--brand" disabled={!!busy} onClick={() => submit('ask')}>
            {busy === 'ask' ? 'Asking'
              : scopedTo ? `Ask ${scopedTo.displayName.split(' ')[0]}`
              : 'Ask the board'}
          </button>
          <button className="btn btn--ghost" disabled={!!busy} onClick={() => submit('table')}>
            {busy === 'table' ? 'Saving' : 'Put it on the table'}
          </button>
      </div>

      <div className="ask__tools">
        <button
          className="ask__tool"
          type="button"
          disabled={!!busy || files.length >= 4}
          aria-label="Attach a file to this question"
          title={files.length >= 4 ? 'Four files is the limit' : 'Attach a file to this question'}
          onClick={() => picker.current?.click()}
        >
          <IconClip />
        </button>

        {/*
          Only in the app, and it is most of the reason there is an app.

          The owner's numbers arrive as a photograph of a statement taken in a van. The
          paperclip beside this opens the file sheet, which on iOS is two taps and a
          decision away from the shutter; this is the shutter. On the website it is not
          drawn at all, because there is nothing behind it.
        */}
        {isNativeApp() && (
          <button
            className="ask__tool"
            type="button"
            disabled={!!busy || files.length >= 4}
            aria-label="Photograph a document"
            title={files.length >= 4 ? 'Four files is the limit' : 'Photograph a document'}
            onClick={() => void takePhoto()}
          >
            <IconCamera />
          </button>
        )}

        <input
          ref={picker}
          type="file"
          multiple
          hidden
          accept={ASK_ACCEPT}
          onChange={e => { addFiles(e.target.files); e.target.value = '' }}
        />
      </div>
      </div>

      {/* Quiet, under the buttons. The chair handling it is right nearly every time. */}
      {!scopedTo && board.length > 0 && (
        <div className="ask__who">
          <span>The chair takes it.</span>
          <button className="link" onClick={() => setPicking(true)}>Ask someone in particular</button>
        </div>
      )}


      {picking && (
        <SeatPicker
          board={board}
          chosen={scopedTo}
          onChoose={onScopeAsk}
          onClose={() => setPicking(false)}
        />
      )}

      <Recently board={board} onOpen={onAsked} />
    </section>
  )
}

/*
  What you have already asked. Home had no history surface at all: a consult was
  answered, the sheet closed, and the exchange became unreachable without leaving the
  page. Three is enough to recognise the last thing you asked and get back into it.
*/
function Recently({
  board, onOpen,
}: {
  board: BoardMember[]
  onOpen: (consultId: string) => void
}) {
  const [consults, setConsults] = useState<Consult[] | null>(null)

  useEffect(() => {
    void endpoints.consults().then(setConsults).catch(() => setConsults([]))
  }, [])

  return (
    <div className="recently">
      <span className="w__title">Recently</span>

      {consults === null ? (
        <Skeleton rows={2} />
      ) : consults.length === 0 ? (
        <p className="w__empty">Nothing yet. Ask them something.</p>
      ) : (
        <ul className="stack">
          {distinct(consults).slice(0, 3).map(c => {
            const answered = board.find(m => c.membersUsed?.includes(m.id)) ?? null
            return (
              <li key={c.id}>
                <button className="recently__row" onClick={() => onOpen(c.id)}>
                  {answered
                    ? <Avatar member={answered} size="sm" />
                    : <span className="avatar">BS</span>}
                  <span className="recently__q">{c.question}</span>

                  {/*
                    Where the question ended up, which is the only outcome a consult has.

                    A consult is one or two seats answering, not a vote, so there is no
                    tally to report and nothing here claims one. Escalating it is the one
                    decision the owner made about it, and it is worth seeing from the list.
                  */}
                  {c.escalatedItemId
                    ? <span className="recently__tag">On the table</span>
                    : c.status === 'pending'
                      ? <span className="recently__tag recently__tag--live">Thinking</span>
                      : null}

                  <span className="recently__go" aria-hidden>›</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

/*
  Three near-identical questions from an afternoon of testing filled the whole list with
  the same thing. Two questions count as the same when their significant words overlap by
  more than about two thirds, which catches a re-ask and a typo fix without merging two
  genuinely different questions about the same subject.
*/
function distinct(consults: Consult[]): Consult[] {
  const seen: Set<string>[] = []
  const out: Consult[] = []

  for (const c of consults) {
    const words = significant(c.question)
    if (words.size === 0) { out.push(c); continue }

    const dupe = seen.some(prev => {
      let shared = 0
      for (const w of words) if (prev.has(w)) shared += 1
      return shared / Math.min(words.size, prev.size) >= 0.6
    })

    if (!dupe) { seen.push(words); out.push(c) }
  }

  return out
}

/*
  Filler carries no signal and drags the overlap down: two re-asks of the same question
  scored 0.64 against each other purely because they shared "from", "since" and "what".
  Dropping the filler first is what makes a 0.6 threshold mean anything.
*/
const FILLER = new Set([
  'about', 'after', 'again', 'been', 'before', 'being', 'could', 'does', 'doing', 'down',
  'each', 'from', 'have', 'here', 'into', 'just', 'know', 'more', 'much', 'need', 'over',
  'said', 'same', 'should', 'some', 'still', 'such', 'take', 'tell', 'than', 'that',
  'their', 'them', 'then', 'there', 'these', 'they', 'thing', 'this', 'those', 'were',
  'what', 'when', 'where', 'which', 'while', 'will', 'with', 'would', 'your', 'exactly',
  'first', 'really', 'think', 'want', 'like', 'make', 'made', 'because', 'very',
])

function significant(question: string): Set<string> {
  const words = question.toLowerCase().match(/[a-z0-9%$]+/g) ?? []
  return new Set(words.filter(w => w.length > 3 && !FILLER.has(w)))
}

// ─── 3. Next meeting ────────────────────────────────────────────────────────

interface LastDecision {
  meetingId?: string
  headline?: string
  completedAt?: string
}

export function NextMeetingCard({
  next, board, onNavigate, onCapture,
}: {
  next: NextMeeting | null
  board: BoardMember[]
  onNavigate: (screen: 'meetings' | 'table') => void
  onCapture: () => void
}) {
  const [last, setLast] = useState<LastDecision | null>(null)

  useEffect(() => {
    void endpoints.lastDecision().then(setLast).catch(() => setLast(null))
  }, [])

  // The chair reports the recommendation, so the chair's face goes against it. The
  // minutes record no single deciding member, and captioning one would be a small lie.
  const chair = board.find(m => m.seatRole === 'chair') ?? null

  if (!next) return <section className="w w--meeting"><Skeleton rows={3} /></section>

  const when = next.scheduledFor ? new Date(next.scheduledFor) : null
  /*
    Five slots, always.

    It was Math.max(5, itemCount), so a nine-item agenda drew nine filled dots, and a row
    that is entirely gold communicates exactly as much as one that is entirely empty. The
    row is a gauge: it needs something unfilled to be read against. Past five, the count
    lives in the line underneath, which can say nine without pretending to draw it.
  */
  // Four, because four is what a meeting takes: CurationService.MaxDiscussed. The row is
  // the curated agenda filling up, not the inbox, so its capacity is the real cap.
  const SLOTS = 4
  const filled = Math.min(next.itemCount, SLOTS)
  const prepped = next.itemCount === 0 ? 0 : next.preppedCount / next.itemCount

  const cta =
    next.chipState === 'running' ? { label: 'Join the meeting', go: () => onNavigate('meetings') }
    : next.itemCount === 0       ? { label: 'Put something on the table', go: onCapture }
    : next.chipState === 'curating' ? { label: 'Review the agenda', go: () => onNavigate('table') }
    : { label: 'Review the agenda', go: () => onNavigate('table') }

  return (
    <section className="w w--meeting">
      <WidgetHeader title="Next meeting" action="All meetings" onNavigate={() => onNavigate('meetings')} />

      <div className="meeting__when">{when ? weekday(when) : 'Not scheduled'}</div>
      <div className="meeting__sub">
        {when && <>{clock(when)} · <span className="meeting__countdown">{countdown(when)}</span></>}
      </div>

      {/*
        The fill indicator. It used to be one 6px dot, which read as a rendering artefact
        rather than as a gauge. Filled is an item on the agenda, hollow is room for more.
      */}
      <div className="dots" role="img" aria-label={`${next.itemCount} on the agenda`}>
        {Array.from({ length: SLOTS }, (_, i) => (
          <span key={i} className={`dot ${i < filled ? 'is-filled' : ''}`} />
        ))}
      </div>

      <div className="meeting__count">{fillLine(next)}</div>

      {/*
        Who will be in the room.

        The one piece of imagery on Home that is load-bearing rather than decorative. The
        product's claim is that five people argue about your business, and until this strip
        the only evidence on the anchor card was the word "board". Faces on the card the
        whole screen is arranged around say it without a sentence.

        Portraits, not initials: the seats are generated from their own attribute vectors,
        so the one that will tell you to swing looks like it. Initials are still the
        fallback inside Avatar for a seat an owner built from scratch.
      */}
      {board.length > 0 && (
        <div className="meeting__seats">
          <span className="meeting__seats-row" aria-hidden>
            {board.slice(0, 6).map(m => (
              <span key={m.id} className="meeting__seat" style={ramp(m.colorRamp)}>
                <Avatar member={m} size="sm" />
              </span>
            ))}
          </span>
          <span className="meeting__seats-line">
            {seatsLine(board.length)}
          </span>
        </div>
      )}

      {/*
        Only once there is prep to show.

        It rendered whenever anything was on the agenda, so before the Wednesday prep run
        it drew a full-width track with a zero-width fill: a thin rule under the count that
        looked like a divider somebody forgot to style. The line above already says "prep
        starts Wednesday", which is the honest state, and an empty gauge adds nothing to it.
      */}
      {next.preppedCount > 0 && (
        <div className="bar" role="img" aria-label={`${next.preppedCount} of ${next.itemCount} prepped`}>
          <span className="bar__fill" style={{ width: `${Math.round(prepped * 100)}%` }} />
        </div>
      )}

      <button className="btn btn--onbrand" onClick={cta.go}>{cta.label}</button>

      {last?.headline && (
        <div className="meeting__last" style={chair ? ramp(chair.colorRamp) : undefined}>
          <span className="meeting__last-label">Last time</span>
          <div className="meeting__last-row">
            {chair && <Avatar member={chair} size="sm" />}
            {/* No character cut. .meeting__last-text clamps at two lines, and the box
                knows how wide it is where a count of 150 does not. */}
            <span className="meeting__last-text">{tidy(last.headline)}</span>
          </div>
        </div>
      )}
    </section>
  )
}

/*
  Spelled, not numeric. A board is five people, and "5 seats" reads like a quantity of
  stock. Minutes.tsx has its own copy of this for the same reason; six words is not worth
  a shared module and a cross-screen import.
*/
const SEATS = ['no', 'one', 'two', 'three', 'four', 'five', 'six']

function seatsLine(n: number): string {
  const word = SEATS[n] ?? String(n)
  return `${word.charAt(0).toUpperCase()}${word.slice(1)} ${n === 1 ? 'seat' : 'seats'} at the table`
}

/*
  §4 copy: never show "prepped 0". Before prep runs the honest line is when it will,
  which is also the answer to the question the zero was accidentally raising.
*/
function fillLine(next: NextMeeting): string {
  const items = `${next.itemCount} ${next.itemCount === 1 ? 'item' : 'items'}`

  if (next.itemCount === 0) return 'Nothing on the table yet'
  if (next.preppedCount === 0) return `${items} · prep starts Wednesday`
  if (next.preppedCount < next.itemCount) return `${items} · ${next.preppedCount} prepped`
  return `${items} · prepped`
}

/*
  Whitespace only. Nothing here decides where the text ends any more.

  Both of the old cutters worked on a character count: clip() at 150 and oneLine() at 77,
  the second of them slicing mid-word. A count cannot know how wide the box is, so on a
  phone they cut early and on a wide card they cut a sentence that had room to finish.
  The clamps in app.css do it instead, at two lines for the quote and one for a table row,
  and they measure the actual space.
*/
function tidy(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

function weekday(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
}

function clock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function countdown(d: Date): string {
  const ms = d.getTime() - Date.now()
  if (ms <= 0) return 'now'

  const hours = Math.round(ms / 3_600_000)
  if (hours < 1) return 'within the hour'
  if (hours < 24) return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`

  const days = Math.round(hours / 24)
  return `in ${days} ${days === 1 ? 'day' : 'days'}`
}

// ─── 4. The Table ───────────────────────────────────────────────────────────

export function TableCard({
  items, onNavigate, onCaptured, onOpenItem,
}: {
  items: AgendaItem[]
  onNavigate: () => void
  onCaptured: (text: string) => Promise<void>
  onOpenItem: (id: string) => void
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)

  const add = async () => {
    const value = text.trim()
    if (!value || busy) return
    setBusy(true)
    try {
      await onCaptured(value)
      setText('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="w w--table">
      <WidgetHeader title="The table" count={items.length} action="Open" onNavigate={onNavigate} />

      {items.length === 0 ? (
        /* §6: not "nothing here". What the board will do anyway is the actual answer,
           and it converts an empty table from a dead end into a reason to add something. */
        <p className="w__empty">
          Add nothing and the board still meets. It will take your numbers and whatever
          you have left open, and set its own agenda.
        </p>
      ) : (
        <TableRail items={items} onNavigate={onNavigate} onOpenItem={onOpenItem} />
      )}

      <div className="quickadd">
        <input
          className="quickadd__box"
          value={text}
          name="table-quick-add"
          autoComplete="off"
          placeholder="Add something for Friday"
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
        />
        <button className="btn" disabled={busy || !text.trim()} onClick={add}>
          {busy ? 'Adding' : 'Add'}
        </button>
      </div>
    </section>
  )
}

/**
 * The table itself, one item at a time, thumbed sideways.
 *
 * It used to be the first two items clamped to a line each, which answered "is there
 * anything on the table" and nothing else. An owner reading two truncated sentences cannot
 * tell what Friday is going to be about, and the count in the header was doing all the real
 * work. One item per card, at a size you can actually read, and the rest a thumb away.
 *
 * ── Why the browser does the sticking ───────────────────────────────────────
 *
 * `scroll-snap-type: x mandatory` on the rail and `scroll-snap-align: center` on the cards
 * is the whole magnet. It runs on the compositor, it keeps the platform's own rubber-banding
 * and momentum, and it cannot fight the finger. Every hand-written version of this listens
 * for scroll end and animates to the nearest card, which on iOS means arguing with a
 * momentum curve the page cannot see.
 *
 * The scroll handler below only reads. It never sets scrollLeft, so it cannot fight the
 * snapping either: all it does is work out which card is in the middle so the dots know
 * which one to fill.
 */
function TableRail({
  items, onNavigate, onOpenItem,
}: {
  items: AgendaItem[]
  onNavigate: () => void
  onOpenItem: (id: string) => void
}) {
  const rail = useRef<HTMLUListElement>(null)
  const [active, setActive] = useState(0)

  /*
    Ten, and a card that says how many are behind it.

    Every item on the table is a DOM node here, and an owner who has been forwarding all
    week can have thirty. Ten is more than anyone thumbs through on a home screen, and the
    screen behind the header is where a long table gets read.
  */
  const shown = items.slice(0, 10)
  const rest = items.length - shown.length

  /*
    Which card is in the middle, measured rather than counted.

    Card widths are a percentage and the gap is a token, so dividing scrollLeft by an
    assumed card width is a sum that goes wrong at the first breakpoint. Nearest centre to
    the rail's centre is true at any width, mid-flick as well as at rest.

    Read inside a rAF because scroll fires far faster than paint, and reading offsetLeft
    from the handler itself is a layout flush on every one of those events.
  */
  useEffect(() => {
    const el = rail.current
    if (!el) return

    let queued = false

    const measure = () => {
      queued = false
      const middle = el.scrollLeft + el.clientWidth / 2
      let nearest = 0
      let best = Infinity

      Array.from(el.children).forEach((child, i) => {
        const card = child as HTMLElement
        const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle)
        if (distance < best) { best = distance; nearest = i }
      })

      setActive(nearest)
    }

    const onScroll = () => {
      if (queued) return
      queued = true
      requestAnimationFrame(measure)
    }

    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [shown.length])

  const goTo = (i: number) => {
    const card = rail.current?.children[i] as HTMLElement | undefined
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }

  return (
    <div className="rail">
      {/*
        A list, still. It is one item per card rather than one item per row, and a screen
        reader should hear the same thing either way: how many are on the table, and each of
        them in order. tabIndex makes the rail itself focusable, which is what gives a
        keyboard the arrow keys inside it for free.
      */}
      <ul
        className="rail__track"
        ref={rail}
        tabIndex={0}
        aria-label={`${items.length} on the table`}
      >
        {shown.map(item => {
          const label = summarise(item.rawText)
          const gist = tidy(item.rawText)

          return (
          <li key={item.id} className="rail__card">
            {/*
              The whole card opens the item. Stretched over the card rather than wrapped
              around it: the card is a flex column whose layout the rail depends on, and a
              button around all of it would have to reproduce that. A drag does not reach
              here, because a browser suppresses the click that ends a scroll gesture.
            */}
            <button
              className="rail__open"
              onClick={() => onOpenItem(item.id)}
              aria-label={`Open: ${oneLine(item.rawText)}`}
            />

            {/*
              The mark and a six-word label share a line, with the owner's sentence under it.

              The icon arrived on a row of its own and left the whole width of the card empty
              beside it, while the sentence below ran five clamped lines that all opened the
              same way on a follow-up ("You said you would: ..."). A flick through the rail
              read as one paragraph repeated, and the icon was the only thing distinguishing
              one card from the next. Same fix as the Table screen's rows: the label is cut
              from the item's own words in their own order, so it can be unhelpful but never
              wrong (see src/summarise.ts), and the sentence is demoted rather than replaced.
            */}
            <div className="rail__head">
              <TopicIcon name={topicFor(item.rawText)} size={34} />
              {label && <span className="rail__label">{label}</span>}
            </div>

            {/* Suppressed when the label already is the sentence, which happens on short
                items: "Raise?" does not want saying twice. */}
            {gist !== label && <p className="rail__text">{gist}</p>}

            <div className="rail__foot">
              <span className={`badge badge--${item.source}`}>{sourceLabel(item)}</span>

              {/* Only when it is true. An item that has been pushed twice is the one
                  thing on this card an owner should feel something about. */}
              {item.deferredCount > 0 && (
                <span className="rail__carried">
                  held over {item.deferredCount === 1 ? 'once' : `${item.deferredCount} times`}
                </span>
              )}

              {item.clarifierQuestion && !item.clarifierAnswer && (
                <span className="rail__asked">a seat has a question</span>
              )}
            </div>
          </li>
          )
        })}

        {rest > 0 && (
          <li className="rail__card rail__card--more">
            <button className="rail__morebtn" onClick={onNavigate}>
              {rest === 1 ? 'One more' : `${rest} more`} on the table
            </button>
          </li>
        )}
      </ul>

      {/*
        Position, and a way to get there with a mouse.

        A trackpad swipes this and a finger flicks it, but a mouse over a horizontal
        scroller does nothing at all in most browsers, so on a desktop these dots are the
        only handle the rail has.
      */}
      {shown.length > 1 && (
        <div className="rail__dots">
          {Array.from({ length: shown.length + (rest > 0 ? 1 : 0) }).map((_, i) => (
            <button
              key={i}
              className={`rail__dot${i === active ? ' is-on' : ''}`}
              aria-label={`Item ${i + 1}`}
              aria-current={i === active}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function sourceLabel(item: AgendaItem): string {
  if (item.source === 'system') return 'board flagged'
  if (item.source === 'escalation') return 'from a consult'
  // A forward is still the owner asking, but it arrived without them opening the app, and
  // the badge is the only place that fact is visible. Worth saying: it is how somebody
  // recognises the thread they sent from their phone at six in the morning.
  if (item.source === 'email') return 'forwarded'
  return 'you'
}

/** Kept for aria-labels, which have no box to be clamped by. */
function oneLine(text: string): string {
  return tidy(text)
}

// ─── 5. Actions ─────────────────────────────────────────────────────────────

export function YourWordCard({
  commitments, onNavigate, onResolve,
}: {
  commitments: Commitment[]
  onNavigate: () => void
  onResolve: (id: string, status: string, note?: string) => Promise<void>
}) {
  const [closing, setClosing] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const sorted = [...commitments].sort((a, b) => {
    const at = a.dueDate ? parseDay(a.dueDate).getTime() : Infinity
    const bt = b.dueDate ? parseDay(b.dueDate).getTime() : Infinity
    return at - bt
  })

  const midnight = new Date()
  midnight.setHours(0, 0, 0, 0)
  const overdue = sorted.filter(
    c => c.status === 'open' && c.dueDate && parseDay(c.dueDate) < midnight,
  ).length

  return (
    <section className="w w--word">
      {/* Loud only when something is actually late. A card that always shouts is a card
          nobody hears. */}
      <WidgetHeader
        title={overdue > 0 ? (overdue === 1 ? '1 action past due' : `${overdue} actions past due`) : 'Actions'}
        count={overdue > 0 ? undefined : sorted.length}
        live={overdue > 0}
        action="All of them"
        onNavigate={onNavigate}
      />

      {sorted.length === 0 ? (
        <p className="w__empty">Nothing outstanding. Nothing owed.</p>
      ) : (
        <ul className="stack">
          {sorted.slice(0, 3).map(c => {
            const overdue = c.dueDate ? daysUntil(c.dueDate) < 0 : false
            return (
              <li key={c.id} className="stack__row stack__row--word">
                <label className="tick">
                  <input
                    type="checkbox"
                    checked={closing === c.id}
                    onChange={e => { setClosing(e.target.checked ? c.id : null); setNote('') }}
                    aria-label={`Mark done: ${oneLine(c.text)}`}
                  />
                </label>

                <div className="word__body">
                  <span className="stack__text">{oneLine(c.text)}</span>
                  <span className={`word__due ${overdue ? 'is-overdue' : ''}`}>
                    {c.dueDate ? due(c.dueDate, overdue) : 'no date set'}
                  </span>

                  {c.followUpBy && c.followUpOn && (
                    <span className="word__promise">
                      {c.followUpBy.split(' ')[0]}'s putting this on the table Friday.
                    </span>
                  )}

                  {closing === c.id && (
                    <div className="quickadd">
                      <input
                        className="quickadd__box"
                        autoFocus
                        name="commitment-outcome"
                        autoComplete="off"
                        value={note}
                        placeholder="What happened?"
                        onChange={e => setNote(e.target.value)}
                        onKeyDown={async e => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            await onResolve(c.id, 'done', note.trim() || undefined)
                            setClosing(null)
                          }
                        }}
                      />
                      <button
                        className="btn"
                        onClick={async () => {
                          await onResolve(c.id, 'done', note.trim() || undefined)
                          setClosing(null)
                        }}
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {/* Capped so a long list cannot drive the height of the whole row. */}
      {sorted.length > 3 && (
        <button className="w__more w__more--inline" onClick={onNavigate}>
          <span>{sorted.length - 3} more</span>
          <span aria-hidden>›</span>
        </button>
      )}
    </section>
  )
}

function due(iso: string, overdue: boolean): string {
  const label = formatDay(iso)
  return overdue ? `was due ${label}` : `due ${label}`
}

// ─── 6. Company ─────────────────────────────────────────────────────────────

/*
  What the board flagged, and nothing else.

  The tiles moved to the masthead strip. A number belongs at the top of the screen where it
  is read on the way past, not in a card two thirds down where it has to be looked up, and
  showing the same three figures in both places would be worse than either. What is left
  here is the one thing that is not a number: a movement large enough that a seat has been
  put on it, with the sentence they wrote.

  Home does the fetch now and hands it to both readers, so this costs no extra call.
*/
export function CompanyCard({
  metrics, onCaptured,
}: {
  metrics: HomeMetrics | null
  onCaptured: (text: string) => Promise<void>
}) {
  if (!metrics) return null

  /*
    Nothing on file, nothing on screen.

    This used to render a card explaining that it had no numbers, which is the one thing a
    card about numbers should never be. A visitor being shown the product reads a widget as
    a claim about the business in front of them, so an empty one is worse than an absent
    one, and a populated one had better be populated with figures this business actually
    stated. The Numbers is now the source, so a card only appears once there is something
    true to put in it.
  */
  // No flag, no card. The figures themselves are in the masthead strip.
  if (!metrics.anomaly) return null

  return (
    <section className="w w--company">
      <WidgetHeader title="A number moved" live />

      {metrics.anomaly && (
        <div className="anomaly" style={ramp(metrics.anomaly.flaggedRamp ?? 'gray')}>
          <div className="anomaly__who">
            {metrics.anomaly.flaggedFace && (
              <img className="promise__face" src={metrics.anomaly.flaggedFace} alt="" loading="lazy" />
            )}
            <span className="anomaly__text">
              {/*
                The narrative once a member has written one. Until then, the plain fact.

                Not "Sam flagged your close rate", which is what this said and which nobody
                did: the movement is found by arithmetic in AnomalyDetector, and the seat is
                chosen afterwards by FollowUpSelection as whoever scores highest on the
                ground it touches. Putting a sentence in a named person's mouth that they
                never said is the same failure as showing a number nobody reported.
              */}
              {metrics.anomaly.narrative
                ?? `Your ${metrics.anomaly.label.toLowerCase()} moved enough for the board to flag it. `
                   + `${firstNameOf(metrics.anomaly.flaggedBy)} owns this one, and nobody has explained it yet.`}
            </span>
          </div>
          <button
            className="btn"
            onClick={() => onCaptured(
              `${metrics.anomaly!.label} moved and the board flagged it. What is going on?`)}
          >
            Add to Friday
          </button>
        </div>
      )}
    </section>
  )
}



function firstNameOf(name: string | null): string {
  return name ? (name.split(/\s+/)[0] ?? 'The board') : 'The board'
}
